import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { QRCodeCanvas } from "qrcode.react";
import * as services from "./../../services/customer";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import QRCode from "qrcode";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function QrGeneratorPage() {
  const [amount, setAmount] = useState(1);
  const [qrList, setQrList] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [printFilter, setPrintFilter] = useState("all");

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQr, setSelectedQr] = useState(null);

  const getQrUrl = (id) => `https://scan-test.akka.getkaset.com/scan-bag?id=${id}`;

  // ================= FETCH =================
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await services.getQrcode();

      const list = res.data.data.map((item) => ({
        id: item.uid,
        value: item.uid,
        createdAt: item.createdAt,
        isPrint: item.isPrint || false,
      }));

      setQrList(list);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= FILTER =================
  const filteredList = useMemo(() => {
    return qrList.filter((qr) => {
      const created = new Date(qr.createdAt);

      const start = startDate ? new Date(startDate + "T00:00:00") : null;
      const end = endDate ? new Date(endDate + "T23:59:59") : null;

      if (start && created < start) return false;
      if (end && created > end) return false;
      if (printFilter === "printed" && !qr.isPrint) return false;

      if (printFilter === "notPrinted" && qr.isPrint) return false;

      return true;
    });
  }, [qrList, startDate, endDate, printFilter]);

  // ================= GENERATE =================
  const handleGenerate = async () => {
    try {
      const res = await services.generateQrcode(amount);

      const newQrs = res.data.data.map((item) => ({
        id: item.uid,
        value: item.uid,
        createdAt: item.createdAt,
        printed: false,
      }));

      setQrList((prev) => [...newQrs, ...prev]);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= PRINT =================
  const handlePrint = async (qr) => {
    const selected = qrList.filter((q) => selectionModel.includes(q.id));

    if (selected.length === 0) {
      alert("กรุณาเลือก QR");
      return;
    }

    const printWindow = window.open("", "_blank");

    const html = `
    <html>
      <head>
        <title>Sticker QR</title>
       <style>
    @page {
      size: 100mm 100mm;
      margin: 0;
    }

    html, body {
      margin: 0;
      padding: 0;
      width: 100mm;
      height: 100mm;
    }

    .page {
  width: 100mm;
  height: 100mm;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  page-break-after: always;

  border: 3px solid black;
  box-sizing: border-box;
}

    img {
      width: 80mm;
      height: 80mm;
    }

   .uid {
  font-size: 20px;
  font-weight: bold;
  margin-top: 8px;
}
  </style>
      </head>
      <body>
        ${await Promise.all(
          selected.map(async (q) => {
            const base64 = await QRCode.toDataURL(getQrUrl(q.value));
            return `
              <div class="page">
                <img src="${base64}" />
                <div class="uid">${q.value}</div>
              </div>
            `;
          }),
        ).then((arr) => arr.join(""))}
      </body>
    </html>
  `;

    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        // optional
        printWindow.close();
      }, 500);
    };

    // mark printed
    try {
      await services.markPrinted(selectionModel);

      // update UI
      setQrList((prev) =>
        prev.map((q) =>
          selectionModel.includes(q.id) ? { ...q, printed: true } : q,
        ),
      );

      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  // ================= COLUMNS =================

  const columns = [
    {
      field: "no",
      headerName: "ลำดับ",
      width: 80,
      renderCell: (params) =>
        filteredList.findIndex((q) => q.id === params.row.id) + 1,
    },
    { field: "value", headerName: "UID", flex: 1 },
    {
      field: "createdAt",
      headerName: "วันที่สร้าง",
      width: 180,
      valueGetter: (value) => new Date(value).toLocaleString("th-TH"),
    },
    {
      field: "isPrint",
      headerName: "สถานะ",
      width: 120,
      renderCell: (p) => (
        <Typography color={p.row.isPrint ? "green" : "error"}>
          {p.row.isPrint ? "พิมพ์แล้ว" : "ยังไม่พิมพ์"}
        </Typography>
      ),
    },
    {
      field: "view",
      headerName: "ดู",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation(); // 🔥 กันไม่ให้ trigger row click
            setSelectedQr(params.row);
            setOpenDialog(true);
          }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box p={3}>
      <AppBreadcrumbs />

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" mb={2} pt={2}>
        <Typography variant="h5">จัดการ QR Code</Typography>

        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            type="number"
            label="จำนวน"
            value={amount}
            onChange={(e) => setAmount(+e.target.value)}
            sx={{ width: 100 }}
          />
          <Button variant="contained" onClick={handleGenerate}>
            สร้าง
          </Button>
          <Button variant="contained" color="secondary" onClick={handlePrint}>
            พิมพ์
          </Button>
        </Stack>
      </Box>

      {/* FILTER */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          type="date"
          label="จาก"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="ถึง"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>สถานะพิมพ์</InputLabel>

          <Select
            value={printFilter}
            label="สถานะพิมพ์"
            onChange={(e) => setPrintFilter(e.target.value)}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            <MenuItem value="printed">พิมพ์แล้ว</MenuItem>
            <MenuItem value="notPrinted">ยังไม่พิมพ์</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Typography mb={1}>
        แสดง {filteredList.length} / {qrList.length}
      </Typography>

      {/* TABLE */}
      <DataGrid
        rows={filteredList}
        columns={columns}
        checkboxSelection
        onRowSelectionModelChange={(ids) => setSelectionModel(ids)}
        autoHeight
      />
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
      >
        <DialogTitle>QR Code</DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {selectedQr && (
            <>
              <QRCodeCanvas value={getQrUrl(selectedQr.value)} size={220} />

              <Typography>{selectedQr.value}</Typography>

              <Typography color="text.secondary">
                {new Date(selectedQr.createdAt).toLocaleString("th-TH")}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
